import type {
  AcademyCategory,
  CatalogQuery,
} from "@nyntax/contracts/schemas/course";
import { cn } from "@nyntax/design-system/lib/utils";
import Link from "next/link";
import type { IconType } from "react-icons";
import {
  LuBookOpen,
  LuBriefcase,
  LuClipboardCheck,
  LuCpu,
  LuLanguages,
  LuLayoutGrid,
  LuPalette,
  LuShapes,
  LuSprout,
} from "react-icons/lu";

import type { CatalogViewOptions } from "@/lib/catalog-query";
import { catalogPath } from "@/lib/catalog-query";

/** One icon per category, so the chips read at a glance. */
export const CATEGORY_ICONS: Record<AcademyCategory, IconType> = {
  academics: LuBookOpen,
  "exam-prep": LuClipboardCheck,
  language: LuLanguages,
  technology: LuCpu,
  business: LuBriefcase,
  design: LuPalette,
  "personal-development": LuSprout,
  other: LuShapes,
};

interface CategoryNavProps {
  allLabel: string;
  categories: { label: string; value: AcademyCategory }[];
  label: string;
  locale: string;
  query: CatalogQuery;
  view: CatalogViewOptions;
}

const chipClass = (current: boolean) =>
  cn(
    "inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors",
    "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
    current
      ? "border-foreground bg-foreground text-background"
      : "bg-card hover:border-foreground/30 hover:bg-muted"
  );

/**
 * The category row: "All" plus one chip per category, each with an icon. It
 * scrolls sideways on a phone and keeps the search, sort and free filter.
 *
 * @param props - The categories, the active query and the labels.
 * @returns The category navigation.
 */
export const CategoryNav = ({
  allLabel,
  categories,
  label,
  locale,
  query,
  view,
}: CategoryNavProps) => (
  <nav aria-label={label} className="relative">
    <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">
      <li className="shrink-0">
        <Link
          aria-current={query.category ? undefined : "page"}
          className={chipClass(!query.category)}
          href={catalogPath(locale, { q: query.q }, view)}
        >
          <LuLayoutGrid aria-hidden="true" className="size-4" />
          {allLabel}
        </Link>
      </li>
      {categories.map((category) => {
        const current = query.category === category.value;
        const Icon = CATEGORY_ICONS[category.value];

        return (
          <li className="shrink-0" key={category.value}>
            <Link
              aria-current={current ? "page" : undefined}
              className={chipClass(current)}
              href={catalogPath(locale, { q: query.q, category: category.value }, view)}
            >
              <Icon aria-hidden="true" className="size-4" />
              {category.label}
            </Link>
          </li>
        );
      })}
    </ul>
  </nav>
);
